
import { useState, useEffect } from "react";
import { Event } from "@/types/event";
import { datesArrayToStrings } from "@/utils/dateUtils";
import { EventImage } from "@/components/create-event/form/image-upload/imageUploadUtils";
import { useToast } from "@/hooks/use-toast";

export const useEventFormFields = (
  event: Event,
  onChange: (updatedEvent: Event) => void
) => {
  const [titleError, setTitleError] = useState("");
  const { toast } = useToast();
  
  // Add effect to log event data when it changes
  useEffect(() => {
    console.log('useEventFormFields: Current event deadline:', event.event_deadline);
  }, [event.event_deadline]);

  // Map event data to form fields
  const handleFieldChange = (field: string, value: any) => {
    const updatedEvent = { ...event };

    switch (field) {
      case "title":
        updatedEvent.name = value;
        break;
      case "description":
        updatedEvent.description = value;
        break;
      case "date":
        updatedEvent.event_date = value?.toISOString();
        break;
      case "preferredDates":
        // Use our utility function to convert Date objects to strings in local timezone
        updatedEvent.preferred_dates = datesArrayToStrings(value);
        break;
      case "preferredTimeOfDay":
        updatedEvent.preferred_time_of_day = value;
        break;
      case "eventLocation":
        if (updatedEvent.location_type === 'flexible') {
          updatedEvent.flexible_location_description = value;
        }
        break;
      case "locationType":
        updatedEvent.location_type = value;
        // Clear location-related fields when switching types
        if (value === 'specific') {
          updatedEvent.flexible_location_description = null;
        } else {
          updatedEvent.preferred_venues = [];
        }
        break;
      case "preferredVenues":
        if (updatedEvent.location_type === 'specific') {
          updatedEvent.preferred_venues = value;
        }
        break;
      case "locationNote":
        updatedEvent.flexible_location_description = value;
        break;
      case "imageUrl":
        updatedEvent.image_url = value;
        break;
      case "imageUrls":
        // Handle image URLs array updates - ensure we're working with arrays
        if (value && Array.isArray(value)) {
          // Ensure additional_images is initialized as an array
          if (!updatedEvent.additional_images || !Array.isArray(updatedEvent.additional_images)) {
            updatedEvent.additional_images = [];
          }
          
          // Update non-primary images
          const newNonPrimaryImages = value.map((url: string, index: number) => ({
            image_id: `temp-${index}`,
            image_url: url,
            is_primary: false,
            display_order: index + 1 // Start at 1 because primary is 0
          }));
          
          // Find primary image if exists
          const primaryImage = Array.isArray(updatedEvent.additional_images) ? 
            updatedEvent.additional_images.find((img: EventImage) => img?.is_primary) : 
            undefined;
          
          // Update the additional_images with primary and non-primary images
          updatedEvent.additional_images = [
            ...(primaryImage ? [primaryImage] : []),
            ...newNonPrimaryImages
          ];
        }
        break;
      case "eventImages":
        // Handle direct updates to the eventImages array - ensure we're working with arrays
        if (value) {
          // Handle both function and direct array updates
          if (typeof value === 'function') {
            // If value is a function, apply it to current additional_images
            const currentImages = Array.isArray(updatedEvent.additional_images) ? 
              updatedEvent.additional_images : [];
            updatedEvent.additional_images = value(currentImages);
          } else if (Array.isArray(value)) {
            // If value is an array, use it directly
            updatedEvent.additional_images = value;
          }
          
          // Also update the primary image URL for compatibility
          if (Array.isArray(updatedEvent.additional_images)) {
            const primaryImage = updatedEvent.additional_images.find((img: EventImage) => img?.is_primary);
            if (primaryImage) {
              updatedEvent.image_url = primaryImage.image_url;
            }
          }
        }
        break;
      case "guestList":
        // Add handling for guest list changes
        // Initialize guests as an empty array if it doesn't exist
        if (!updatedEvent.guests) {
          updatedEvent.guests = [];
        }
        updatedEvent.guests = value;
        break;
      case "eventDeadline":
        // Handle the event deadline field
        // Convert Date object to ISO string format when saving to database
        const oldDeadline = updatedEvent.event_deadline;
        updatedEvent.event_deadline = value ? value.toISOString() : null;
        
        console.log('Setting eventDeadline:', {
          old: oldDeadline,
          new: updatedEvent.event_deadline,
          valueType: value ? typeof value : 'null'
        });
        
        // Show toast notification for deadline changes
        if (oldDeadline !== updatedEvent.event_deadline) {
          toast({
            title: value ? "Deadline updated" : "Deadline removed",
            description: value ? 
              `RSVP deadline set to ${value.toLocaleString()}` : 
              "RSVP deadline has been removed",
          });
        }
        break;
    }

    onChange(updatedEvent);
  };

  // Safely extract image data from event
  const getEventImageData = () => {
    // Ensure additional_images is always an array
    const safeEventImages = Array.isArray(event.additional_images) ? 
      event.additional_images : [];

    // Safely extract non-primary image URLs
    const nonPrimaryImageUrls = safeEventImages
      .filter((img: EventImage) => img && !img.is_primary)
      .map((img: EventImage) => img.image_url);

    return {
      safeEventImages,
      nonPrimaryImageUrls
    };
  };

  return {
    titleError,
    setTitleError,
    handleFieldChange,
    getEventImageData
  };
};
